import React, { useState, useEffect, useCallback, useRef } from "react";
import { Search } from "lucide-react";
import { useParams } from "react-router-dom";
import CustomChatComponent from "../../components/ChatComponent";
import { useAuth } from "../../context/AuthContext";
import { streamClient } from "../../config/stream";
import Sidebar from "../../components/Sidebar";
import { ClipLoader } from "react-spinners";
import { getChannelDisplayName } from "../../services/streamService";
import { getUserChannels } from "../../api/examPrepApi";
import NotificationDropdown from "../../components/NotificationDropdown";
import { useTranslation } from "react-i18next";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { safeMarkChannelRead } from "../../services/streamConnectionService";

const StudentsTutor = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { channelId: urlChannelId } = useParams();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedChatInfo, setSelectedChatInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("standard");
  const [, setUnreadCounts] = useState({}); 
  const [streamClientConnected, setStreamClientConnected] = useState(false);
  
  // Track if we've already initialized the tab for the current URL channel
  const tabInitializedRef = useRef(false);
  const channelSelectedRef = useRef(false);
  const lastUrlChannelIdRef = useRef(null);

  useEffect(() => {
    const savedActiveTab = localStorage.getItem("activetab");
    if (savedActiveTab) {
      setActiveTab(savedActiveTab);
    }
    
    // Reset refs when URL channel changes
    if (lastUrlChannelIdRef.current !== urlChannelId) {
      tabInitializedRef.current = false;
      channelSelectedRef.current = false;
      lastUrlChannelIdRef.current = urlChannelId;
    }
  }, [urlChannelId]);

  // Check if Stream client is connected
  useEffect(() => {
    const checkConnection = () => {
      if (streamClient && streamClient.userID) {
        setStreamClientConnected(true);
      }
    };
    checkConnection();
    // Check periodically in case connection happens after mount
    const interval = setInterval(checkConnection, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleChannelLeave = (channelId) => {
    setChannels((prevChannels) =>
      prevChannels.filter((channel) => channel.id !== channelId),
    );

    if (selectedChannel?.id === channelId) {
      const remainingChannels = channels.filter(
        (channel) => channel.id !== channelId,
      );

      if (remainingChannels.length > 0) {
        const firstChannel = remainingChannels[0];
        setSelectedChannel(firstChannel);
        const otherUser = getOtherUserFromChannel(firstChannel);
        setSelectedChatInfo(otherUser);
      } else {
        setSelectedChannel(null);
        setSelectedChatInfo(null);
      }
    }
  };

  // Helper function to get the other user (student) for chatInfo
  const getOtherUserFromChannel = useCallback((channel) => {
    if (!channel || !user) return null;
    if (channel.type === "exam_prep") {
      const members = channel.members || [];
      const student = members.find((m) => m.userType !== "tutor");
      if (student) {
        return {
          id: student.id,
          name: student.name,
          image: student.image,
          online: student.online || false,
        };
      }
      return {
        id: "",
        name: "Student",
        image: "/images/exam-prep-icon.png",
        online: false,
      };
    }
    if (channel.type === "one_to_one_chat") {
      const members = Object.values(channel.state?.members || {});
      const student = members.find(
        (member) => member.user?.userType !== "tutor",
      );
      if (student && student.user) {
        return {
          id: student.user.id,
          name: student.user.name,
          image: student.user.image,
          online: student.user.online || false,
        };
      }
    }
    // For group/class chats, always use channel.data.name
    return {
      id: channel.id,
      name: channel.data?.name || channel.name || "Group",
      image: channel.data?.image || "/default-avatar.png",
      online: false,
    };
  }, [user]);

  useEffect(() => {
    const fetchUserChannels = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        // getUserChannels now queries GetStream directly and enriches with Firestore data
        // It also verifies membership and filters out channels where user is not a member
        const channels = await getUserChannels(user.uid);

        // All channels should now have proper data from getUserChannels
        // Verify all have data property and log any missing
        const channelsWithoutData = channels.filter((ch) => !ch.data);
        if (channelsWithoutData.length > 0) {
          console.warn(
            `[StudentsTutor] ${channelsWithoutData.length} channels still missing data property after enrichment`,
          );
        }

        setChannels(channels);
      } catch (err) {
        console.error("[StudentsTutor] Error fetching channels:", err);
        setChannels([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUserChannels();

    // Listen for custom events when tutor creates a group or class
    const handleGroupOrClassCreated = async (event) => {
      const { userId } = event.detail || {};
      if (userId === user?.uid) {
        // Refresh channels when tutor creates a group or class
        // Add a small delay to allow Stream to sync the new channel
        setTimeout(async () => {
          try {
            const channels = await getUserChannels(user.uid);
            setChannels(channels);
          } catch (err) {
            console.error("[StudentsTutor] Error refreshing channels:", err);
          }
        }, 1500); // Wait 1.5 seconds for Stream to sync
      }
    };

    // Add event listeners for group/class creation events
    window.addEventListener("tutorGroupCreated", handleGroupOrClassCreated);
    window.addEventListener("tutorClassCreated", handleGroupOrClassCreated);

    return () => {
      window.removeEventListener("tutorGroupCreated", handleGroupOrClassCreated);
      window.removeEventListener("tutorClassCreated", handleGroupOrClassCreated);
    };
  }, [user]);

  const handleChannelSelect = useCallback(
    async (channelData, forceTab = null) => {
      if (!streamClientConnected) {
        console.warn(
          "[StudentsTutor] Tried to select channel before Stream client connected",
        );
        return;
      }
      
      // If we have a forced tab (from navigation), use it strictly
      let correctTab = forceTab;
      
      // Only auto-detect if no forced tab was provided
      if (!correctTab || (correctTab !== "standard" && correctTab !== "bammbuu")) {
        const channelType = channelData.type || channelData?.type;
        correctTab = "standard"; // Default to student chats tab
        
        // Premium groups go to group chats tab
        if (channelType === "premium_group") {
          correctTab = "bammbuu";
        } else if (channelType === "one_to_one_chat") {
          // One-on-one chats with students go to student chats tab
          correctTab = "standard";
        }
      }
      
      // Update tab if it's different from current
      if (activeTab !== correctTab) {
        setActiveTab(correctTab);
        localStorage.setItem("activetab", correctTab);
      }
      
      // Create a real Stream channel instance
      const streamChannel = streamClient.channel(
        channelData.type,
        channelData.id,
      );
      await streamChannel.watch();
      
      // Ensure user is a member of the channel
      const members = streamChannel.state.members || {};
      if (!members[user.uid]) {
        try {
          await streamChannel.addMembers([user.uid]);
        } catch (err) {
          console.error("[StudentsTutor] Error adding user to channel:", err);
        }
      }
      
      setSelectedChannel(streamChannel);
      setSelectedChatInfo(getOtherUserFromChannel(channelData));
      
      try {
        await safeMarkChannelRead(streamChannel);
        setUnreadCounts((prev) => ({
          ...prev,
          [channelData.id]: 0,
        }));
      } catch (error) {
        console.error("[StudentsTutor] Error marking channel as read:", error);
      }
    },
    [streamClientConnected, user.uid, getOtherUserFromChannel, activeTab],
  );

  // Handle URL-based channel selection
  useEffect(() => {
    // Only initialize tab and select channel on first load or when URL channel changes
    if (urlChannelId && streamClientConnected && !channelSelectedRef.current && channels.length > 0) {
      const savedTab = localStorage.getItem("activetab");
      
      // Find the channel with the matching ID
      const targetChannel = channels.find(
        (channel) => channel.id === urlChannelId,
      );
      
      // If channels are loaded and we found the target, select it
      if (targetChannel) {
        // Determine the correct tab by checking channel type first, then Firestore if needed
        const determineTab = async () => {
          let tabToUse = null;
          
          // First, check if we have a channel with a type we can use
          const channelType = targetChannel.type;
          if (channelType === "premium_group") {
            tabToUse = "bammbuu";
          } else if (channelType === "standard_group") {
            tabToUse = "standard";
          } else if (channelType === "one_to_one_chat") {
            // One-on-one chats with students go to student chats tab
            tabToUse = "standard";
          }
          
          // If we couldn't determine from channel type, check Firestore
          if (!tabToUse) {
            try {
              const groupDoc = await getDoc(doc(db, "groups", urlChannelId));
              if (groupDoc.exists()) {
                const groupData = groupDoc.data();
                const isPremium = groupData.isPremium;
                tabToUse = isPremium ? "bammbuu" : "standard";
                localStorage.setItem("activetab", tabToUse);
              } else {
                // Use saved tab if available, otherwise default to standard
                tabToUse = savedTab || "standard";
              }
            } catch (err) {
              console.error("[StudentsTutor] Error checking Firestore:", err);
              tabToUse = savedTab || "standard";
            }
          } else {
            // Save the determined tab for consistency
            localStorage.setItem("activetab", tabToUse);
          }
          
          // Mark as selected before calling handleChannelSelect to prevent re-triggering
          channelSelectedRef.current = true;
          if (!tabInitializedRef.current) {
            tabInitializedRef.current = true;
          }
          
          handleChannelSelect(targetChannel, tabToUse);
        };
        
        determineTab();
      } else if (channels.length > 0) {
        // Channels loaded but target channel not found - try querying directly
        const determineTab = async () => {
          const savedTab = localStorage.getItem("activetab");
          let tabToUse = savedTab || "bammbuu"; // Default to group chats for premium groups
          
          // Try to query the channel directly from Stream
          try {
            let groupData = null;
            // Check Firestore first to determine if it's a premium group
            const groupDoc = await getDoc(doc(db, "groups", urlChannelId));
            if (groupDoc.exists()) {
              groupData = groupDoc.data();
              tabToUse = groupData.isPremium ? "bammbuu" : "standard";
              localStorage.setItem("activetab", tabToUse);
            }
            
            // Try premium_group first (most common for tutors)
            let streamChannel = streamClient.channel("premium_group", urlChannelId);
            await streamChannel.watch();
            
            // Add the channel to the channels list if it's not already there
            // This ensures it appears in the sidebar
            setChannels((prevChannels) => {
              const exists = prevChannels.some((ch) => ch.id === streamChannel.id);
              if (!exists) {
                // Enrich with basic data from Firestore if available
                const enrichedChannel = {
                  ...streamChannel,
                  data: {
                    ...streamChannel.data,
                    name: streamChannel.data?.name || groupData?.groupName || "Group",
                    image: streamChannel.data?.image || groupData?.imageUrl || "/default-avatar.png",
                    description: streamChannel.data?.description || groupData?.groupDescription || "",
                    language: groupData?.groupLearningLanguage || "English",
                  },
                };
                return [...prevChannels, enrichedChannel];
              }
              return prevChannels;
            });
            
            channelSelectedRef.current = true;
            if (!tabInitializedRef.current) {
              tabInitializedRef.current = true;
            }
            handleChannelSelect(streamChannel, tabToUse);
          } catch (error) {
            console.error(
              "[StudentsTutor] Failed to query channel directly:",
              error,
            );
            channelSelectedRef.current = true;
          }
        };
        
        determineTab();
      }
    }
  }, [urlChannelId, streamClientConnected, channels, handleChannelSelect]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter channels based on type and search query
  const filterChannels = (channelsToFilter) => {
    return channelsToFilter.filter((channel) => {
      // Safeguard: ensure channel.data exists, if not, add an empty data object
      if (!channel.data || typeof channel.data !== "object") {
        channel.data = {};
      }

      let searchName = "";
      if (channel.type === "one_to_one_chat") {
        // Use the other user's name for search
        const members = Object.values(channel.state?.members || {});
        const otherMember = members.find(
          (member) => member.user?.id !== user.uid,
        );
        searchName =
          otherMember && otherMember.user
            ? otherMember.user.name?.toLowerCase() || ""
            : "";
      } else {
        searchName = (channel.data?.name || "").toLowerCase();
      }
      const channelDescription = (
        channel.data?.description || ""
      ).toLowerCase();
      const query = searchQuery.toLowerCase();
      return searchName.includes(query) || channelDescription.includes(query);
    });
  };

  // Helper to check if a one_to_one_chat is with a student (tutor view)
  const isStudentChat = (channel) => {
    if (channel.type === "one_to_one_chat") {
      const members = Object.values(channel.state?.members || {});
      const otherMember = members.find(
        (member) => member.user?.id !== user?.uid,
      );
      // Check if the other user is a student (not a tutor)
      return otherMember?.user?.userType !== "tutor";
    }
    return false;
  };

  // Categorize channels for tutor view
  // Student chats: one-on-one chats with students only
  const standardChats = channels.filter((ch) => {
    // Only include one_to_one_chat if it's with a student
    if (ch.type === "one_to_one_chat") {
      return isStudentChat(ch);
    }
    return false;
  });
  
  // Group chats: premium groups that the tutor is part of
  const bammbuuChats = channels.filter((ch) => {
    // Only include premium groups
    return ch.type === "premium_group";
  });

  // Helper function to extract the student from a channel
  const getStudentFromChannel = (channel) => {
    if (!channel || !user) return null;
    const members = Object.values(channel.state?.members || {});
    // Find the member who is not the tutor
    const student = members.find((m) => m.user?.id !== user.uid);
    if (student && student.user) {
      return {
        id: student.user.id,
        name: student.user.name,
        image: student.user.image,
        online: student.user.online || false,
      };
    }
    return {
      id: "",
      name: "Student",
      image: "/images/exam-prep-icon.png",
      online: false,
    };
  };

  // Helper to filter out hidden system messages
  const filterVisibleMessages = (messages) => {
    return messages.filter(
      (msg) => msg.type !== "hidden_system" && !msg.hidden,
    );
  };

  const ChatItem = ({ channel }) => {
    let name = "Group";
    let image = "/default-avatar.png";
    let isGroupChat = false;

    if (channel.type === "exam_prep" || channel.type === "one_to_one_chat") {
      const student = getStudentFromChannel(channel);
      name =
        channel.type === "exam_prep"
          ? `Exam Prep - ${student.name}`
          : student.name;
      image = student.image;
    } else if (channel.data?.name) {
      name = channel.data.name;
      image = channel.data.image || "/default-avatar.png";
      isGroupChat = true; // This is a group/class chat
    }

    // Get language for group chats
    const groupLanguage = channel.data?.language || "English";

    let languageFlag = "/svgs/xs-us.svg";
    if (groupLanguage === "Spanish") {
      languageFlag = "/svgs/xs-spain.svg";
    } else if (
      groupLanguage === "English/Spanish" ||
      groupLanguage === "English-Spanish" ||
      groupLanguage === "English-Spanish Exchange"
    ) {
      languageFlag = "/svgs/eng-spanish-xs.svg";
    }

    // Get member count for group chats
    const memberCount =
      channel.members?.length ||
      Object.keys(channel.state?.members || {}).length ||
      "-";

    return (
      <div
        key={channel.id}
        className={`flex cursor-pointer items-center gap-3 rounded-3xl border p-3 ${
          selectedChannel?.id === channel.id
            ? "border-[#22bf37] bg-[#f0fdf1]"
            : "border-[#fbbf12] bg-white"
        }`}
        onClick={() => handleChannelSelect(channel, null)}
      >
        <div className="relative">
          <img
            src={image}
            alt={name}
            className="h-12 w-12 rounded-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default-avatar.png";
            }}
          />
        </div>
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span className="truncate text-lg font-bold">{name}</span>
          </div>
          {isGroupChat && (
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1">
                <img
                  src={languageFlag}
                  alt={groupLanguage}
                  className="h-5 w-5"
                />
                <span className="text-sm font-medium text-gray-700">
                  {groupLanguage}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <img src="/svgs/users.svg" alt="members" className="h-5 w-5" />
                <span className="text-sm font-medium text-gray-700">
                  {memberCount}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-white">
        <div className="h-full w-[272px] flex-shrink-0 p-4">
          <Sidebar user={user} />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <ClipLoader color="#FFB800" size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar */}
      <div className="h-full w-[272px] flex-shrink-0 p-4">
        <Sidebar user={user} />
      </div>

      {/* Main Content */}
      <div className="min-w-[calc(100% - 272px)] h-[calc(100vh-0px)] flex-1 overflow-x-auto p-4 pl-0">
        <div className="h-[calc(100vh-32px)] overflow-y-auto rounded-3xl border border-[#e7e7e7] bg-white p-[16px]">
          <div className="mb-6 flex items-center justify-between border-b pb-4">
            <div className="flex items-center gap-4">
              <h1 className="text-4xl font-semibold">Students</h1>
            </div>
            <div className="flex flex-shrink-0 items-center gap-4">
              <NotificationDropdown />
            </div>
          </div>

          <div className="m-2 flex h-[calc(100vh-166px)] flex-1 rounded-3xl bg-white">
            <div className="flex w-96 flex-col overflow-hidden rounded-2xl bg-[#f6f6f6] p-4">
              <div className="mb-10 flex w-full justify-center sm:w-auto">
                <div className="relative inline-flex rounded-full border border-[#888888] bg-white p-1">
                  <div
                    className="absolute left-0 top-0 h-full rounded-full border border-[#042F0C] bg-[#FFBF00] transition-all duration-300 ease-in-out"
                    style={{
                      transform:
                        activeTab === "standard"
                          ? "translateX(0)"
                          : "translateX(82.0%)",
                      width: activeTab === "standard" ? "47.5%" : "55%",
                    }}
                  />
                  <button
                    onClick={() => setActiveTab("standard")}
                    className="relative z-10 whitespace-nowrap rounded-full px-[22px] py-1 text-sm font-medium text-[#042f0c] transition-colors"
                  >
                    {t("tutor-chats.tabs.freeChat")}
                  </button>
                  <button
                    onClick={() => setActiveTab("bammbuu")}
                    className="relative z-10 whitespace-nowrap rounded-full px-6 py-1 font-medium text-black transition-colors"
                  >
                    {t("tutor-chats.tabs.tutorChats")}
                  </button>
                </div>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-3 h-5 w-5 text-[#5d5d5d]" />
                <input
                  type="text"
                  placeholder={
                    activeTab === "standard"
                      ? t("tutor-chats.search.freeChats")
                      : t("tutor-chats.search.tutorChats")
                  }
                  className="w-full rounded-3xl border border-gray-200 py-2 pl-12 pr-4 focus:border-[#14B82C] focus:outline-none focus:ring-0"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="scrollbar-hide flex-1 space-y-2 overflow-y-auto">
                {filterChannels(
                  activeTab === "standard" ? standardChats : bammbuuChats,
                ).map((channel) => (
                  <ChatItem key={channel.id} channel={channel} />
                ))}
              </div>
            </div>

            <div className="ml-4 flex-1">
              {selectedChannel ? (
                <CustomChatComponent
                  channelId={selectedChannel.id}
                  type={selectedChannel.type}
                  onChannelLeave={handleChannelLeave}
                  chatInfo={selectedChatInfo}
                  description={selectedChannel.data?.description || ""}
                  name={
                    selectedChannel.type === "exam_prep"
                      ? `Exam Prep - ${getStudentFromChannel(selectedChannel)?.name || "Student"}`
                      : getChannelDisplayName(selectedChannel, user)
                  }
                  filterMessages={filterVisibleMessages}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-gray-500">
                  {t("tutor-chats.messages.selectChat")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentsTutor;
